import { useTheme } from "next-themes";
import { T, F, th as palette } from "@/lib/bricks";

import { isPolycamHost, isSplatFileUrl, visitFromShareUrl } from "@/lib/visitBridge";
import { useMemo } from "react";
import VisitExperience from "@/components/visit/VisitExperience";
import { normalizeManifest } from "@/lib/visitManifest";
import VisitPasswordGate from "@/components/visit/VisitPasswordGate";
import { useVisitManifest } from "@/components/visit/useVisitManifest";

export { isPolycamHost, isSplatFileUrl, visitFromShareUrl };

// ─── Polycam open CTA (iframe blocked by Polycam) ───────────────────

function PolycamOpenCard({ url, dark, p }) {
  return (
    <div
      data-testid="visit-polycam-cta"
      style={{
        width: "100%",
        padding: "28px 22px",
        background: dark ? "#0a0a0a" : "#F4F1EB",
        border: `1px solid ${dark ? "rgba(244,241,235,0.14)" : "rgba(20,17,16,0.12)"}`,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <div style={{ fontSize: 12, color: p.ink3 }}>
        Polycam bridge
      </div>
      <div style={{ fontFamily: F.display, fontSize: 15, fontWeight: 500, color: p.ink, lineHeight: 1.45, maxWidth: 480 }}>
        Free-roam Virtual Visit opens on Polycam. Owned GPU cook is not live yet — this is the bridge path.
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontFamily: F.display,
          fontSize: 14,
          fontWeight: 500,
          padding: "12px 16px",
          borderRadius: 2,
          border: "none",
          background: T.brick,
          color: "#FBEFE8",
          textDecoration: "none",
        }}
      >
        Open Virtual Visit
      </a>
    </div>
  );
}

// ─── Generic http embed (non-Polycam) ────────────────────────────────

function GenericEmbed({ url }) {
  return (
    <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%", background: "#0a0a0a", borderRadius: 2 }}>
      <iframe
        src={url}
        title="Virtual Visit"
        allow="xr-spatial-tracking; fullscreen"
        allowFullScreen
        style={{
          position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
          border: "none", borderRadius: 2,
        }}
      />
    </div>
  );
}

// ─── Gaussian splat Visit (shared Spark viewer) ─────────────────────
// gsplat.js 1.2.9 was replaced 2026-10-11: the old code called Splat.Load
// (does not exist) and new WebGLRenderer({width,height}) (wrong signature),
// so no file Visit ever rendered. Spark loads .sog/.spz/.ply/.splat.

function SplatVisit({ visit, unbranded, desk }) {
  // Desk rows already carry signed rooms/file_url (auth API, password or not):
  // build the manifest locally. Public pages ask /api/public/visits/{id}.
  const local = desk || (Array.isArray(visit.rooms) && visit.rooms.length > 0);
  const { state, manifest: fetched, unlock } = useVisitManifest({
    visitId: visit.id,
    fileUrl: visit.file_url || null,
    unbranded,
    enabled: !local,
  });
  const localManifest = useMemo(
    () => (local ? normalizeManifest({ ...visit, unbranded: true }) : null),
    [local, visit.id, visit.file_url, visit.rooms]
  );
  if (local) {
    if (!localManifest) return null;
    return <VisitExperience manifest={localManifest} fallbackUrl={visit.file_url || null} />;
  }
  if (state === "locked") return <VisitPasswordGate onUnlock={unlock} />;
  if (state === "loading" || state === "idle") {
    return <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%", background: "#0b0a09", borderRadius: 2 }} />;
  }
  if (!fetched) return null;
  return <VisitExperience manifest={fetched} fallbackUrl={visit.file_url || null} />;
}

// ─── VisitViewer ───────────────────────────────────────────────────

export default function VisitViewer({ visit, unbranded = false, desk = false }) {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  const p = palette(dark);

  if (!visit) return null;

  if (visit.type === "url" && visit.embed_url) {
    const url = String(visit.embed_url).trim();
    if (isPolycamHost(url)) {
      return <PolycamOpenCard url={url} dark={dark} p={p} />;
    }
    return <GenericEmbed url={url} />;
  }

  if (visit.type === "file" && (visit.file_url || visit.id)) {
    return <SplatVisit visit={visit} unbranded={unbranded} desk={desk} />;
  }

  return (
    <div style={{ padding: "48px 0", textAlign: "center", fontSize: 13, color: p.ink3 }}>
      No visit source available
    </div>
  );
}
